import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Trash2, ShieldAlert, ShieldCheck } from "lucide-react";

export default function AccountPrivacy() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const exportData = async () => {
    setLoadingExport(true);
    try {
      const { data } = await api.get("/auth/me/personal-data");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `proxireduc28-mes-donnees-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Vos données ont été téléchargées.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'export."); }
    finally { setLoadingExport(false); }
  };

  const deleteAccount = async () => {
    const ans = prompt('Cette action supprime définitivement votre compte et anonymise vos données. Tapez "SUPPRIMER" pour confirmer.');
    if (ans !== "SUPPRIMER") return;
    setLoadingDelete(true);
    try {
      await api.delete("/auth/me");
      toast.success("Votre compte a été supprimé.");
      await refresh();
      navigate("/", { replace: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de la suppression."); }
    finally { setLoadingDelete(false); }
  };

  const isSuperAdmin = user?.role === "super_admin";

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="account-privacy-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center"><ShieldCheck size={20}/></div>
        <div>
          <h1 className="font-heading text-2xl font-bold">Confidentialité & données</h1>
          <p className="text-xs text-slate-500">Gérez vos données personnelles conformément au RGPD.</p>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <div className="p-4 border border-slate-200 rounded-xl">
          <div className="flex items-start gap-3">
            <Download size={18} className="text-slate-700 shrink-0 mt-0.5"/>
            <div className="flex-1">
              <div className="font-semibold">Exporter mes données</div>
              <p className="text-xs text-slate-500 mt-1">
                Téléchargez toutes les données que nous détenons vous concernant (profil, favoris, notifications, demandes de contact) au format JSON.
              </p>
            </div>
            <Button onClick={exportData} disabled={loadingExport} variant="outline" data-testid="privacy-export-btn">
              {loadingExport ? "…" : "Exporter"}
            </Button>
          </div>
        </div>

        <div className="p-4 border border-rose-200 bg-rose-50/40 rounded-xl">
          <div className="flex items-start gap-3">
            <ShieldAlert size={18} className="text-rose-700 shrink-0 mt-0.5"/>
            <div className="flex-1">
              <div className="font-semibold text-rose-900">Supprimer mon compte</div>
              <p className="text-xs text-slate-600 mt-1">
                Cette action est irréversible. Votre email, nom, ville et coordonnées seront anonymisés,
                vos favoris et notifications supprimés. Vos éventuelles demandes de contact seront également anonymisées.
                {isSuperAdmin && <span className="block mt-1 text-rose-700 font-semibold">Le compte super-administrateur ne peut pas être supprimé depuis cette page.</span>}
              </p>
            </div>
            <Button onClick={deleteAccount} disabled={loadingDelete || isSuperAdmin}
                    className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="privacy-delete-btn">
              <Trash2 size={14} className="mr-1"/> {loadingDelete ? "…" : "Supprimer"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
